// Where this DEVICE can go, not just where the active identity can.
//
// Better Auth's multiSession plugin keeps every identity that has been proved on
// the device, and `/api/me` returns all of them: each one's seats, whether it is a
// superadmin, and the token that makes it active. Four screens have to read that
// the same way — the workspace switcher, `/home`, `/admin`, and a workspace's own
// door — so the reading lives here rather than four times.
//
// **The thing this exists to stop.** Signing in to a second workspace under a
// second address does not sign the first one out, but every screen behaved as
// though it did: `/admin` showed its email form to somebody whose superadmin
// session was already in the cookie jar, and a workspace's door asked for an
// address the device had proved an hour earlier. Both then mailed a link to
// re-prove it. A credential you already hold should never be asked for again, so
// where an identity on this device can open the thing being asked for, the app
// offers the hop instead of the form.
//
// Being listed here grants nothing. It is the membership projection in D1, a cache
// of every workspace's roster; access is decided against the live log on arrival,
// so a seat that has since been withdrawn gets the sign-in screen like anyone else.

import { api, type Me } from './api'

/** One workspace this device can open, and who it would open as. */
export interface Reachable {
  slug: string
  name: string
  /** `null` when the ACTIVE identity holds this seat: no switch needed. */
  token: string | null
  /** the address it opens as, when that is not the active one */
  via: string | null
}

/** Every workspace any identity on this device has a team seat in. The active
 *  identity's own seat wins, so the common case involves no switch at all. */
export function reachableWorkspaces(me: Me | null): Reachable[] {
  const out: Reachable[] = []
  for (const s of me?.seats ?? []) {
    if (s.kind === 'internal') out.push({ slug: s.slug, name: s.name, token: null, via: null })
  }
  for (const id of me?.identities ?? []) {
    for (const s of id.seats) {
      if (s.kind !== 'internal') continue
      if (out.some((o) => o.slug === s.slug)) continue
      out.push({ slug: s.slug, name: s.name, token: id.token, via: id.email })
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name))
}

/** How this device reaches one workspace, or `null` when nobody here can. */
export function hopTo(me: Me | null, slug: string): Reachable | null {
  return reachableWorkspaces(me).find((r) => r.slug === slug) ?? null
}

/** The identity on this device that may manage workspaces, if any. `token` is
 *  `null` when it is the active one. */
export function superadminHop(me: Me | null): { token: string | null; email: string } | null {
  if (!me) return null
  if (me.superadmin) return { token: null, email: me.me.email }
  const other = me.identities.find((i) => i.superadmin)
  return other ? { token: other.token, email: other.email } : null
}

/**
 * Become another identity, then go.
 *
 * A full page load rather than a re-render: the store, the roster decision and the
 * brand were all resolved for the identity being replaced, and the session cookie
 * has just changed under them.
 */
export async function goAs(token: string | null, path: string): Promise<void> {
  if (token) await api.switchIdentity(token)
  location.assign(path)
}
